import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Button, ButtonContainer, Container, Divider, EditableDataGrid, GridRow, GridItem, Icon, Link, Spacer, Stack, Typography } from '@emburse/embark-core';
import { AddIcon } from '@emburse/embark-icons';
import { DeleteOutline } from '@mui/icons-material';
import UploadDownload from '../../../../views/QuickSetup/components/UploadDownload';
import DataGridLoading from '../../../../views/QuickSetup/components/DataGridLoading';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { generateRandomGUID } from '../../../../util/helpers';
import { defaultValidation } from '../../../../util/formValidation';
const Departments = forwardRef(({ data, loading, upload, download, alert }, ref) => {
    useTranslation();
    const [tableData, setTableData] = useState([]);
    useEffect(() => setTableData(data || []), [data]);
    const rowValidation = (row) => {
        const nameInvalid = defaultValidation(row.Name);
        const name = nameInvalid.length && { field: 'Name', reason: nameInvalid };
        return [name];
    };
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        },
        isValid() {
            const mappedTable = tableData.map((item, index) => {
                const filteredValidation = rowValidation(item).filter((cell) => !!cell);
                const invalid = filteredValidation.length > 0;
                return (invalid && {
                    row: index + 1,
                    cell: filteredValidation
                });
            });
            return mappedTable.filter((item) => !!item);
        }
    }));
    const addRow = () => setTableData((prevState) => [
        { id: generateRandomGUID(), Name: '', Code: '' },
        ...prevState
    ]);
    const rowChangeHandler = (row) => setTableData((prevState) => prevState.map((tRow) => (tRow.id === row.id ? row : tRow)));
    const deleteRow = (id) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const tableRow = tableDataCopy.findIndex((row) => row.id === id);
        tableDataCopy.splice(tableRow, 1);
        setTableData(tableDataCopy);
    };
    const moreActionsMenu = (params) => ({
        sections: [
            {
                items: [
                    {
                        iconText: 'Delete',
                        closeOnClick: true,
                        icon: _jsx(DeleteOutline, {}),
                        onClick: () => deleteRow(params.row.id)
                    }
                ]
            }
        ]
    });
    const saveRow = async (id, row) => {
        const invalid = rowValidation(row).every((item) => !!item);
        // TEditableDGError
        if (invalid) {
            return {
                success: false,
                error: rowValidation(row)
            };
        }
        rowChangeHandler(row);
        return { success: true };
    };
    const preProcessCells = async (params) => {
        if (!params.hasChanged) {
            return { ...params.props };
        }
        const invalid = defaultValidation(params.props.value);
        return { ...params.props, error: invalid.length ? invalid : false };
    };
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(Typography, { variant: "h4", children: $t('Departments') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Every user and expense in Certify must be assigned to a department. Customers typically align departments 
                in Certify with their internal Department or Cost Center structure. For example: Sales, Marketing, 
                and Finance are three commonly used departments.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`A user's default department can also be used to control which expense categories 
                they are able to view and assign to an expense - we'll set that up later.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Use the table below to add, edit, or remove departments. If you have a lot of departments, 
                consider using the Bulk Upload buttons on the right to download an Excel template, populate it with your departments, and upload it to Certify.`) }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('Read more about departments '), _jsx(Link, { href: "https://help.certify.com/hc/en-us/articles/15461028171277-Create-a-New-Department", target: "_blank", children: $t('here') }), "."] }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 35 }), _jsxs(GridRow, { justifyContent: "space-between", children: [_jsx(GridItem, { children: _jsx(Typography, { variant: "h5", children: $t('Departments') }) }), _jsx(GridItem, { children: _jsx(UploadDownload, { upload: upload, download: download }) })] }), _jsx(Spacer, { size: 35 }), alert.show && (_jsx(Alert, { alertTitle: alert.title, severity: alert.severity, action: _jsx(Button, { color: alert.severity, onClick: alert.onClick, children: $t('See All Errors') }), children: alert.text })), !!tableData.length && (_jsx(ButtonContainer, { alignButtons: "right", sx: { position: 'relative' }, children: _jsx(Button, { variant: "text", size: "small", onClick: addRow, endIcon: _jsx(Icon, { iconUrl: AddIcon }), style: {
                        position: 'absolute',
                        zIndex: 99,
                        right: 0,
                        top: '12px'
                    }, children: $t('Add Row') }) })), _jsx(EditableDataGrid, { autoHeight: true, pageSizeOptions: [5, 10, 100], rows: tableData, generateMoreActionsMenu: (params) => moreActionsMenu(params), onSave: saveRow, hideOnSaveSuccessSnackbar: true, localeText: { noRowsLabel: '' }, columns: [
                    {
                        field: 'Name',
                        headerName: $t('Department Name'),
                        flex: 6,
                        editable: true,
                        type: 'string',
                        preProcessEditCellProps: preProcessCells
                    },
                    {
                        field: 'Code',
                        headerName: $t('Department Code'),
                        flex: 6,
                        editable: true,
                        type: 'string'
                    }
                ] }), !tableData.length && !loading && (_jsxs(Stack, { spacing: 2, direction: "column", justifyContent: "space-between", sx: {
                    marginTop: '-72px',
                    zIndex: 99,
                    position: 'relative'
                }, children: [_jsx(Button, { size: "medium", onClick: addRow, children: $t('Enter Data Manually') }), _jsx(Typography, { variant: "body2", children: $t('You will need to add ALL data manually') })] })), loading && _jsx(DataGridLoading, {}), _jsx(Spacer, { size: 35 }), _jsx(Typography, { variant: "body2", children: $t('You need to create a minimum of 1 department.') })] }));
});
export default Departments;
